import dayjs from 'dayjs'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { i18n } from '@/i18n/index'

/** 秒级 Unix 时间戳；NaN/非有限值视为无效，便于用 ?? 与默认值兜底 */
export function unixSecondsOrUndefined (v: unknown): number | undefined {
  if (v == null || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n)) return undefined
  return n
}

export function formatTime (time: number) {
  if (!time) {
    return ''
  }
  const date = new Date(time * 1000)
  const format = (num: number) => num < 10 ? '0' + num : num
  return `${date.getFullYear()}-${format(date.getMonth() + 1)}-${format(date.getDate())} ${format(date.getHours())}:${format(date.getMinutes())}`
}

/**
 * 补全时间戳
 * @description 给10位时间戳补零，补到13位
 * @param timeStamp
 */
export function completedTimeStamp (timeStamp: string | number) {
  timeStamp = String(timeStamp)
  const timeStampLength = 13
  if (timeStamp.length < timeStampLength) {
    timeStamp = timeStamp + '000'
  }
  return Number(timeStamp)
}

/**
 * 格式化时间戳
 * @param timeStamp
 * @param rule
 * @returns
 */
export function formatTimeStamp (timeStamp: string | number, rule = 'YYYY-MM-DD HH:mm:ss') {
  /** 产品要求大于1980年的时间才展示 */
  if (!timeStamp || Number(completedTimeStamp(timeStamp)) < dayjs('1980').valueOf()) {
    return ''
  }

  const ts = completedTimeStamp(timeStamp)
  return dayjs(ts).format(rule)
}

/**
 * @param duration 秒数
 */
export function formatDuration (duration: number) {
  const HOUR_SEC = 3600
  const MIN_SEC = 60
  const { t } = i18n.global
  let outStr = ''
  if (duration > 0) {
    outStr += Math.floor(duration / HOUR_SEC) ? (Math.floor(duration / HOUR_SEC) + t('common.unitHour')) : ''
    outStr += Math.floor((duration % HOUR_SEC) / MIN_SEC) ? (Math.floor((duration % HOUR_SEC) / MIN_SEC) + t('common.unitMinute')) : ''
    outStr += (duration % HOUR_SEC) % MIN_SEC ? +(duration % HOUR_SEC) % MIN_SEC + t('common.unitSecond') : ''
    return outStr
  }
  return '--'
}

export function getDayStart (date: Date): any {
  if (!date) {
    return date
  }
  const temp = new Date(date)
  temp.setHours(0, 0, 0, 0)
  return temp
}

export function getDayEnd (date: Date): any {
  if (!date) {
    return date
  }
  const temp = new Date(date)
  temp.setHours(23, 59, 59, 999)
  return temp
}

export function countdownDay (second) {
  const day = Math.ceil((Number(second) - dayjs().unix()) / 3600 / 24)
  return Math.max(day, 0)
}

export function countdownText (milliSecond) {
  const { t } = i18n.global
  let str = ''
  let day = dayjs(milliSecond).diff(dayjs(), 'days')
  let hour = dayjs(milliSecond).diff(dayjs(), 'hours') % 24
  let minute = dayjs(milliSecond).diff(dayjs(), 'minutes') % 60
  let second = dayjs(milliSecond).diff(dayjs(), 'seconds') % 60
  if (day > 0) {
    str = `${day}${t('common.unitDay')}${hour}${t('common.unitHour')}${minute}${t('common.unitMinute')}${second}${t('common.unitSecond')}`
  } else if (hour > 0) {
    str = `${hour}${t('common.unitHour')}${minute}${t('common.unitMinute')}${second}${t('common.unitSecond')}`
  } else if (minute > 0) {
    str = `${minute}${t('common.unitMinute')}${second}${t('common.unitSecond')}`
  } else if (second > 0) {
    str = `${second}${t('common.unitSecond')}`
  } else {
    str = '--'
  }
  return str
}

/**
 * 根据时间范围枚举获取时间区间（秒数，数组存储）
 * @param timeRange 时间范围枚举值
 * @returns [start_time, end_time] 秒数时间戳数组，如果为全部则返回 [undefined, undefined]
 */
export function getTimeRangeByEnum (timeRange: TimeRange | string): [number | undefined, number | undefined] {
  if (timeRange === TimeRange.ALL || timeRange === 'all') {
    return [undefined, undefined]
  }

  const now = Math.floor(dayjs().valueOf() / 1000)
  let startTime: number

  switch (timeRange) {
    case TimeRange.HOURS_24:
      startTime = Math.floor(dayjs().subtract(24, 'hour').valueOf() / 1000)
      break
    case TimeRange.DAYS_7:
      startTime = Math.floor(dayjs().subtract(7, 'day').valueOf() / 1000)
      break
    case TimeRange.DAYS_30:
      startTime = Math.floor(dayjs().subtract(30, 'day').valueOf() / 1000)
      break
    default:
      startTime = Math.floor(dayjs().subtract(24, 'hour').valueOf() / 1000)
  }

  return [startTime, now]
}

/** 日期范围默认值：最近一个月～当日结束（列表筛选用） */
export function defaultOneMonthDateRange (): Date[] {
  return [
    getDayStart(new Date(dayjs().subtract(1, 'month').valueOf())),
    getDayEnd(new Date())
  ]
}
